import { get, set } from 'idb-keyval';

// Storage keys
const STORAGE_PREFIX = 'barcode-scanner/';
const TOKEN_KEY = STORAGE_PREFIX + 'api-token';
const TOKEN_EXPIRY_KEY = STORAGE_PREFIX + 'api-token-expiry';

// The base URL of the backend API. Can be overridden via the build-time environment.
export const BASE_URL = process.env.BASE_URL || 'https://devapp.elite-ll.be';

/**
 * Logs into the backend and retrieves a fresh authentication token.
 * The login credentials are expected to be provided at build time via environment variables.
 *
 * @returns {Promise<string>} The newly generated token.
 * @throws {Error} If the login request fails or the response is not successful.
 */
async function login() {
  const login = process.env.API_LOGIN;
  const password = process.env.API_PASSWORD;

  if (!login || !password) {
    throw new Error('Missing API_LOGIN or API_PASSWORD environment variables.');
  }

  const response = await fetch(`${BASE_URL}/API/login.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ login, password })
  });

  const result = await response.json();

  if (result.status === 'success' && result.token) {
    // Token is valid for 1 hour (3600 seconds).
    const expiry = Date.now() + 60 * 60 * 1000;

    await set(TOKEN_KEY, result.token);
    await set(TOKEN_EXPIRY_KEY, expiry);

    return result.token;
  }

  throw new Error(result.message || 'Failed to retrieve token');
}

/**
 * Retrieves a valid authentication token. If the cached token is missing or expired, a new one is generated.
 *
 * @returns {Promise<string>} A valid authentication token.
 */
export async function getToken() {
  const [token, expiry] = await Promise.all([get(TOKEN_KEY), get(TOKEN_EXPIRY_KEY)]);

  if (token && expiry && Date.now() < expiry) {
    return token;
  }

  return login();
} 